import PropTypes from 'prop-types';
import React from 'react';

// material-ui
import { makeStyles, useTheme } from '@mui/styles';
import { Avatar, Box, Button, ButtonBase, useMediaQuery, Typography } from '@mui/material';

// project imports
import LogoSection from '../LogoSection';
import ProfileSection from './ProfileSection';

// assets
import { IconMenu2 } from '@tabler/icons';

// style constant
const useStyles = makeStyles((theme) => ({
    grow: {
        flexGrow: 1
    },
    headerAvatar: {
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        transition: 'all .2s ease-in-out',
        background: 'linear-gradient(135deg, rgba(15, 140, 255, 0.16) 0%, rgba(139, 92, 246, 0.16) 100%)',
        color: theme.palette.primary.main,
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(15, 140, 255, 0.24) 0%, rgba(139, 92, 246, 0.24) 100%)',
            transform: 'scale(1.05)'
        },
        [theme.breakpoints.down('sm')]: {
            width: '40px',
            height: '40px'
        }
    },
    boxContainer: {
        width: '228px',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        [theme.breakpoints.down('md')]: {
            width: 'auto',
            minWidth: 'auto'
        },
        [theme.breakpoints.down('sm')]: {
            width: 'auto',
            minWidth: 'auto'
        }
    },
    logoContainer: {
        [theme.breakpoints.down('sm')]: {
            display: 'none'
        }
    },
    blogLink: {
        [theme.breakpoints.down('sm')]: {
            display: 'none'
        }
    }
}));

// ===========================|| MAIN NAVBAR / HEADER ||=========================== //

const Header = ({ handleLeftDrawerToggle }) => {
    const classes = useStyles();
    const theme = useTheme();
    const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            {/* logo & toggler button */}
            <div className={classes.boxContainer}>
                <Box component="span" className={classes.logoContainer}>
                    <LogoSection />
                </Box>
                <ButtonBase
                    sx={{
                        borderRadius: '12px',
                        overflow: 'hidden',
                        minHeight: matchDownSm ? '44px' : '48px',
                        minWidth: matchDownSm ? '44px' : '48px',
                        background: 'rgba(255, 255, 255, 0.68)',
                        border: '1px solid rgba(15, 140, 255, 0.16)',
                        boxShadow: '0 12px 28px rgba(15, 140, 255, 0.12)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.9)',
                            borderColor: 'rgba(139, 92, 246, 0.28)',
                            transform: 'scale(1.02)'
                        }
                    }}
                >
                    <Avatar
                        variant="rounded"
                        className={classes.headerAvatar}
                        onClick={handleLeftDrawerToggle}
                        color="inherit"
                        sx={{
                            cursor: 'pointer',
                            background: 'transparent',
                            '&:hover': {
                                background: 'transparent'
                            }
                        }}
                    >
                        <IconMenu2 stroke={1.5} size={matchDownSm ? '1.2rem' : '1.3rem'} />
                    </Avatar>
                </ButtonBase>
            </div>

            {/* header search */}
            <div className={classes.grow} />

            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    gap: 1,
                    mr: 2,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    background:
                        'linear-gradient(135deg, rgba(15, 140, 255, 0.12) 0%, rgba(139, 92, 246, 0.14) 54%, rgba(24, 201, 139, 0.12) 100%)',
                    border: '1px solid rgba(255,255,255,0.7)',
                    boxShadow: '0 12px 30px rgba(15, 140, 255, 0.1)'
                }}
            >
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#18c98b',
                        boxShadow: '0 0 0 4px rgba(24, 201, 139, 0.16)'
                    }}
                />
                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 900, letterSpacing: 0 }}>
                    OCR Studio
                </Typography>
            </Box>

            {/* Blog link - hidden on mobile */}
            <Box
                component="span"
                className={classes.blogLink}
                sx={{
                    marginRight: matchDownMd ? 2 : 3
                }}
            >
                <Button
                    variant="outlined"
                    size={matchDownSm ? 'small' : 'medium'}
                    href="https://blog.imagetext.xyz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        textTransform: 'none',
                        fontSize: matchDownSm ? '0.875rem' : '1rem',
                        fontWeight: 600,
                        borderRadius: '12px',
                        borderColor: 'rgba(15, 140, 255, 0.24)',
                        color: theme.palette.primary.main,
                        padding: matchDownSm ? '8px 16px' : '10px 20px',
                        minHeight: matchDownSm ? '40px' : '44px',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.76) 0%, rgba(223, 247, 255, 0.72) 100%)',
                        backdropFilter: 'blur(16px)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, rgba(223, 247, 255, 0.95) 0%, rgba(240, 231, 255, 0.9) 100%)',
                            borderColor: 'rgba(139, 92, 246, 0.42)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 14px 30px rgba(15, 140, 255, 0.16)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    <Typography
                        variant={matchDownSm ? 'body2' : 'body1'}
                        sx={{
                            fontWeight: 600,
                            fontSize: matchDownSm ? '0.875rem' : '1rem',
                            letterSpacing: 0
                        }}
                    >
                        Blog
                    </Typography>
                </Button>
            </Box>

            {/* notification & profile */}
            <ProfileSection />
        </>
    );
};

Header.propTypes = {
    handleLeftDrawerToggle: PropTypes.func
};

export default Header;
